/** Staff security: enroll an authenticator app (TOTP). After enrollment,
 * every sign-in requires password + code. */
import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { request } from "@/platform/api";
import { useStaff } from "./StaffShell";

export function SecurityPage() {
  const staff = useStaff();
  const { toast } = useToast();
  const [setup, setSetup] = useState<{ provisioning_uri: string; secret: string } | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [enabled, setEnabled] = useState(Boolean(staff?.totp_enabled));

  const begin = async () => {
    setBusy(true);
    try {
      setSetup(await request("POST", "/api/v1/platform/auth/staff/totp/enroll"));
    } catch (error) {
      toast({ title: "Could not start enrollment", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await request("POST", "/api/v1/platform/auth/staff/totp/confirm", { code });
      setEnabled(true);
      setSetup(null);
      toast({ title: "Two-factor enabled", description: "Sign-ins now require your authenticator code." });
    } catch (error) {
      toast({ title: "Code not accepted", description: error instanceof Error ? error.message : undefined, variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">Security</h1>
        <p className="text-sm text-muted-foreground">Protect your staff account with an authenticator app.</p>
      </header>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className={`h-5 w-5 ${enabled ? "text-success" : "text-muted-foreground"}`} />
            Two-factor authentication
          </CardTitle>
          <CardDescription>
            {enabled
              ? "Enabled — your sign-ins require password + authenticator code."
              : "Not enabled yet. Strongly recommended for every staff account."}
          </CardDescription>
        </CardHeader>
        {!enabled && (
          <CardContent className="space-y-4">
            {!setup ? (
              <Button onClick={begin} disabled={busy}>
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Set up authenticator"}
              </Button>
            ) : (
              <>
                <div className="rounded-lg border bg-muted/40 p-4 text-sm">
                  <p className="font-medium">1. Add this account to your authenticator app</p>
                  <p className="mt-1 text-muted-foreground">
                    In Google Authenticator / Authy choose "Enter a setup key" and paste:
                  </p>
                  <code className="mt-2 block break-all rounded bg-background p-2 text-xs">{setup.secret}</code>
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium">2. Enter the 6-digit code it shows</p>
                  <Input inputMode="numeric" maxLength={6} className="max-w-[10rem] tracking-[0.4em]"
                    value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
                </div>
                <Button onClick={confirm} disabled={busy || code.length !== 6}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & enable"}
                </Button>
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}
